"use client";

import { useRouter } from "next/navigation";
import { useEffect } from "react";

/**
 * This template is temporary so we can override Next.js links to include `?new=true` query param,
 * todo: please remove once we migrate to the app router
 */
export default function Template({ children }: React.PropsWithChildren) {
  const router = useRouter();

  // Hacky solution taken from this issue:
  // https://github.com/vercel/next.js/discussions/32231#discussioncomment-7284386
  useEffect(() => {
    window.push = (pathname: string) => {
      const url = new URL(pathname, window.location.origin);
      url.searchParams.set("new", "true");
      router.push(`${url.pathname}?${url.searchParams.toString()}`);
    };

    function isAnchorOfCurrentUrl(currentUrl: string, newUrl: string) {
      const currentUrlObj = new URL(currentUrl);
      const newUrlObj = new URL(newUrl);
      // Compare hostname, pathname, and search parameters
      if (
        currentUrlObj.hostname === newUrlObj.hostname &&
        currentUrlObj.pathname === newUrlObj.pathname &&
        currentUrlObj.search === newUrlObj.search
      ) {
        // Check if the new URL is just an anchor of the current URL page
        const currentHash = currentUrlObj.hash;
        const newHash = newUrlObj.hash;
        return (
          currentHash !== newHash &&
          currentUrlObj.href.replace(currentHash, "") ===
            newUrlObj.href.replace(newHash, "")
        );
      }

      return false;
    }

    function findClosestAnchor(
      element: HTMLElement | null
    ): HTMLAnchorElement | null {
      while (element && element.tagName.toLowerCase() !== "a") {
        element = element.parentElement;
      }

      return element as HTMLAnchorElement;
    }

    function handleClick(event: MouseEvent) {
      try {
        const target = event.target as HTMLElement;
        const anchor = findClosestAnchor(target);
        if (anchor) {
          const currentUrl = window.location.href;
          const newUrl = anchor.href;
          const isAnchor = isAnchorOfCurrentUrl(currentUrl, newUrl);
          const isDownloadLink = anchor.download !== "";

          const isPageLeaving = !(
            newUrl === currentUrl ||
            isAnchor ||
            isDownloadLink
          );

          if (isPageLeaving) {
            // Cancel the route change
            event.preventDefault();
            event.stopPropagation();

            const urlWithNew = new URL(newUrl);
            urlWithNew.searchParams.set("new", "true");

            router.push(
              `${urlWithNew.pathname}?${urlWithNew.searchParams.toString()}`
            );
          }
        }
      } catch (err) {
        console.error("Navigation override error:", err);
      }
    }

    // Add the global click event listener
    document.addEventListener("click", handleClick, true);

    // Clean up the global click event listener when the component is unmounted
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [router]);

  return <>{children}</>;
}
